import React, { useContext, useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AuthContext from '../contexts/AuthContext';
import api from '../api';
import Notifications from './Notifications';

const Navbar = () => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);
  const profileRef = useRef(null);
  const notificationsRef = useRef(null);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Fetch notification count
  useEffect(() => {
    if (user) {
      const fetchCount = async () => {
        try {
          const response = await api.get('/notifications');
          setNotificationCount(response.data.length);
        } catch (error) {
          console.error('Error fetching notification count:', error);
        }
      };
      fetchCount();
    }
  }, [user]);

  // Close profile dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setIsProfileOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  // Calculate days remaining in subscription
  const getDaysRemaining = (expiryDate) => {
    if (!expiryDate) return 0;
    const expiry = new Date(expiryDate);
    const today = new Date();
    const diffTime = expiry - today;
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  return (
    <nav style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '0.5rem 1rem',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      boxShadow: '0 4px 20px rgba(102, 126, 234, 0.3)',
      color: 'white'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
        <Link to="/" style={{ color: 'white', textDecoration: 'none', fontWeight: 'bold', fontSize: '1.2rem', display: 'flex', alignItems: 'center' }}>
          <img src="/shorthandlover.png" alt="Steno Logo" style={{ height: '50px', margin: '0px', padding: '0px' }} />
        </Link>
        {user && (
          <div style={{ display: 'flex', gap: '1rem' }}>
            <Link to="/" style={navLinkStyle}>Home</Link>
            <Link to="/tests" style={navLinkStyle}>Tests</Link>
            <Link to="/results" style={navLinkStyle}>Results</Link>
            <Link to="/pricing" style={navLinkStyle}>Refer & Earn - Pricing</Link>
            {user.isAdmin && (
              <>
                <Link to="/admin/dashboard" style={navLinkStyle}>Dashboard</Link>
                <Link to="/admin/upload" style={navLinkStyle}>Upload</Link>
                <Link to="/admin/assign" style={navLinkStyle}>Assign</Link>
                <Link to="/admin/users" style={navLinkStyle}>Users</Link>
                <Link to="/admin/finance" style={navLinkStyle}>Finance</Link>
              </>
            )}
          </div>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
        {user && (
          <button
            onClick={() => setIsNotificationsOpen(!isNotificationsOpen)}
            style={{
              ...profileButtonStyle,
              minWidth: '40px',
              padding: '0.5rem',
              position: 'relative'
            }}
            ref={notificationsRef}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
              <path d="M8 16a2 2 0 0 0 2-2H6a2 2 0 0 0 2 2zM8 1.918l-.91.416c-.445.204-.9.378-1.35.519-.9.28-1.735.62-2.5 1.013l-.225.137-.446.062A2.18 2.18 0 0 0 2 5.5c0 1.323.266 2.59.748 3.732l.225.375.446.062a3.18 3.18 0 0 0 1.25.25h.5V9.5a2.5 2.5 0 0 1 5 0v.5h.5a3.18 3.18 0 0 1 1.25-.25l.446-.062.225-.375A4.18 4.18 0 0 0 14 5.5a2.18 2.18 0 0 0-1.518-2.083l-.446-.062-.225-.137c-.765-.393-1.6-.733-2.5-1.013a18.958 18.958 0 0 0-1.35-.519L8 1.918zM8 6a1 1 0 1 0 0 2 1 1 0 0 0 0-2z"/>
            </svg>
            {notificationCount > 0 && (
              <span style={{
                position: 'absolute',
                top: '-5px',
                right: '-5px',
                backgroundColor: 'red',
                color: 'white',
                borderRadius: '50%',
                width: '18px',
                height: '18px',
                fontSize: '0.7rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 'bold'
              }}>
                {notificationCount}
              </span>
            )}
          </button>
        )}
        {user ? (
          <div style={{ position: 'relative' }} ref={profileRef}>
            <button
              onClick={() => setIsProfileOpen(!isProfileOpen)}
              style={profileButtonStyle}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
                <path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm2-3a2 2 0 1 1-4 0 2 2 0 0 1 4 0zm4 8c0 1-1 1-1 1H3s-1 0-1-1 1-4 6-4 6 3 6 4zm-1-.004c-.001-.246-.154-.986-.832-1.664C11.516 10.68 10.289 10 8 10c-2.29 0-3.516.68-4.168 1.332-.678.678-.83 1.418-.832 1.664h10z"/>
              </svg>
              {user.username}
            </button>

            {isProfileOpen && (
              <div style={dropdownStyle}>
                <div style={dropdownHeaderStyle}>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <h3 style={{ margin: 0, color: '#2c3e50', fontWeight: 'bold' }}>{user.username}</h3>
                    <p style={{ margin: 0, color: '#7f8c8d', fontSize: '0.9rem' }}>{user.email}</p>
                  </div>
                </div>

                <div style={{ marginBottom: '1rem', padding: '0.75rem 0' }}>
                  <h4 style={{ margin: '0 0 0.5rem 0', color: '#2c3e50' }}>Account Info</h4>
                  <p style={{ margin: '0.25rem 0', color: '#7f8c8d', fontSize: '0.9rem' }}><strong>Phone:</strong> {user.phone}</p>
                  <p style={{ margin: '0.25rem 0', color: '#7f8c8d', fontSize: '0.9rem' }}><strong>Role:</strong> 
                    <span style={{ 
                      backgroundColor: user.isAdmin ? '#28a745' : '#6c757d', 
                      color: 'white', 
                      padding: '0.125rem 0.25rem', 
                      borderRadius: '4px', 
                      fontSize: '0.8rem' 
                    }}>
                      {user.isAdmin ? 'Admin' : 'User'}
                    </span>
                  </p>
                </div>

                <div style={{ marginBottom: '1rem', padding: '0.75rem 0', borderTop: '1px solid #eee', borderBottom: '1px solid #eee' }}>
                  <h4 style={{ margin: '0 0 0.5rem 0', color: '#2c3e50' }}>Subscription</h4>
                  <p style={{ margin: '0.25rem 0', color: '#7f8c8d', fontSize: '0.9rem' }}><strong>Type:</strong> 
                    <span style={{ 
                      backgroundColor: user.subscriptionType === 'premium' ? '#8e44ad' : '#6c757d', 
                      color: 'white', 
                      padding: '0.125rem 0.25rem', 
                      borderRadius: '4px', 
                      fontSize: '0.8rem' 
                    }}>
                      {user.subscriptionType || 'None'}
                    </span>
                  </p>
                  {user.subscriptionStart && (
                    <p style={{ margin: '0.25rem 0', color: '#7f8c8d', fontSize: '0.9rem' }}><strong>Start Date:</strong> {formatDate(user.subscriptionStart)}</p>
                  )}
                  {user.subscriptionExpiry && (
                    <p style={{ margin: '0.25rem 0', color: '#7f8c8d', fontSize: '0.9rem' }}>
                      <strong>End Date:</strong> {formatDate(user.subscriptionExpiry)} 
                      <span style={{ 
                        color: getDaysRemaining(user.subscriptionExpiry) > 0 ? '#28a745' : '#e74c3c', 
                        fontWeight: 'bold' 
                      }}>
                        {getDaysRemaining(user.subscriptionExpiry) > 0 ? 
                          ` (${getDaysRemaining(user.subscriptionExpiry)} days remaining)` : 
                          ' (Expired)'}
                      </span>
                    </p>
                  )}
                  {!user.subscriptionStart && <p style={{ margin: '0.25rem 0', color: '#7f8c8d', fontSize: '0.9rem' }}><strong>Status:</strong> No active subscription</p>}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {user.subscriptionType === 'simple' && (
                    <button
                      onClick={() => {
                        setIsProfileOpen(false);
                        navigate('/pricing');
                      }}
                      style={linkButtonStyle}
                    >
                      Upgrade to Premium
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setIsProfileOpen(false);
                      navigate('/pricing');
                    }}
                    style={{
                      ...linkButtonStyle,
                      backgroundColor: '#5a67d8',
                      color: '#fff',
                      borderColor: '#5a67d8'
                    }}
                  >
                    View Plans
                  </button>
                  <button
                    onClick={handleLogout}
                    style={{
                      backgroundColor: '#e74c3c',
                      color: '#fff',
                      border: 'none',
                      padding: '0.75rem',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#c0392b';
                      e.target.style.transform = 'scale(1.02)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = '#e74c3c';
                      e.target.style.transform = 'scale(1)';
                    }}
                  >
                    Logout
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div style={{ display: 'flex', gap: '1rem' }}>
            <Link to="/login" style={navLinkStyle}>Login</Link>
            <Link to="/register" style={navLinkStyle}>Register</Link>
          </div>
        )}
        {user && <Notifications 
          isOpen={isNotificationsOpen} 
          onClose={() => setIsNotificationsOpen(false)} 
          notificationsRef={notificationsRef}
        />}
      </div>
    </nav>
  );
};

const navLinkStyle = {
  color: 'white',
  textDecoration: 'none',
  padding: '0.25rem 0.5rem',
  borderRadius: '8px',
  transition: 'all 0.3s ease',
  fontWeight: '500'
};

const profileButtonStyle = {
  background: 'rgba(255,255,255,0.2)',
  color: 'white',
  border: 'none',
  padding: '0.5rem 1rem',
  borderRadius: '25px',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  backdropFilter: 'blur(10px)',
  transition: 'all 0.3s ease',
  boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
};

const dropdownStyle = {
  position: 'absolute',
  right: 0,
  top: '100%',
  marginTop: '0.5rem',
  backgroundColor: 'white',
  borderRadius: '16px',
  boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
  minWidth: '280px',
  zIndex: 100,
  padding: '1rem',
  border: 'none'
};

const dropdownHeaderStyle = {
  textAlign: 'center',
  marginBottom: '0.5rem',
  paddingBottom: '0.5rem',
  borderBottom: '1px solid #eee'
};

const linkButtonStyle = {
  backgroundColor: 'transparent',
  color: '#667eea',
  border: '1px solid #667eea',
  padding: '0.5rem',
  borderRadius: '8px',
  cursor: 'pointer',
  fontSize: '0.9rem',
  textAlign: 'center',
  transition: 'all 0.3s ease',
  textDecoration: 'none',
  display: 'block'
};

export default Navbar;
